import React, { useState, useEffect } from 'react';
import { X, Calculator, TrendingUp, Percent, Calendar, IndianRupee } from 'lucide-react';

const EMICalculator = ({ isOpen, onClose }) => {
  const [loanAmount, setLoanAmount] = useState(5000000);
  const [interestRate, setInterestRate] = useState(8.5);
  const [loanTenure, setLoanTenure] = useState(20);
  const [emi, setEmi] = useState(0);
  const [totalInterest, setTotalInterest] = useState(0);
  const [totalAmount, setTotalAmount] = useState(0);

  useEffect(() => {
    // Calculate EMI based on loan parameters
    // Intermediate variables (principal, rate, tenure) are derived from dependencies
    // and don't need to be listed separately
    const principal = parseFloat(loanAmount) || 0;
    const rate = (parseFloat(interestRate) || 0) / 12 / 100;
    const tenure = (parseFloat(loanTenure) || 0) * 12;

    if (principal > 0 && rate > 0 && tenure > 0) {
      const emiValue = (principal * rate * Math.pow(1 + rate, tenure)) / (Math.pow(1 + rate, tenure) - 1);
      const totalAmountValue = emiValue * tenure;
      const totalInterestValue = totalAmountValue - principal;

      setEmi(Math.round(emiValue));
      setTotalInterest(Math.round(totalInterestValue));
      setTotalAmount(Math.round(totalAmountValue));
    } else {
      setEmi(0);
      setTotalInterest(0);
      setTotalAmount(0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loanAmount, interestRate, loanTenure]);

  if (!isOpen) return null;

  const formatINR = (num) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0,
    }).format(num);
  };

  const principalPercentage = totalAmount > 0 ? (loanAmount / totalAmount) * 100 : 0;
  const interestPercentage = totalAmount > 0 ? (totalInterest / totalAmount) * 100 : 0;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-gray-200 p-6 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="w-12 h-12 bg-gradient-to-br from-orange-500 to-orange-600 rounded-xl flex items-center justify-center">
              <Calculator className="text-white" size={24} />
            </div>
            <div>
              <h3 className="text-2xl font-bold text-gray-900">EMI Calculator</h3>
              <p className="text-sm text-gray-600">Enter details manually or use sliders</p>
            </div>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors">
            <X size={24} />
          </button>
        </div>

        <div className="p-6">
          <div className="grid lg:grid-cols-2 gap-8">
            <div className="space-y-6">
              {/* Loan Amount Input */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <label className="text-sm font-semibold text-gray-700 flex items-center space-x-2">
                    <IndianRupee size={18} className="text-orange-600" />
                    <span>Loan Amount</span>
                  </label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 font-bold">₹</span>
                    <input
                      type="number"
                      value={loanAmount}
                      onChange={(e) => setLoanAmount(e.target.value)}
                      className="w-36 pl-7 pr-3 py-1 border-2 border-orange-100 rounded-lg font-bold text-orange-700 focus:outline-none focus:border-orange-500"
                    />
                  </div>
                </div>
                <input
                  type="range"
                  min="100000"
                  max="100000000"
                  step="50000"
                  value={loanAmount}
                  onChange={(e) => setLoanAmount(e.target.value)}
                  className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-orange-600"
                />
              </div>

              {/* Interest Rate Input */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <label className="text-sm font-semibold text-gray-700 flex items-center space-x-2">
                    <Percent size={18} className="text-orange-600" />
                    <span>Interest Rate (% P.A.)</span>
                  </label>
                  <div className="relative">
                    <input
                      type="number"
                      step="0.1"
                      value={interestRate}
                      onChange={(e) => setInterestRate(e.target.value)}
                      className="w-24 px-3 py-1 border-2 border-orange-100 rounded-lg font-bold text-orange-700 focus:outline-none focus:border-orange-500 text-right"
                    />
                    <span className="ml-2 font-bold text-orange-700">%</span>
                  </div>
                </div>
                <input
                  type="range"
                  min="5"
                  max="20"
                  step="0.1"
                  value={interestRate}
                  onChange={(e) => setInterestRate(e.target.value)}
                  className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-orange-600"
                />
              </div>

              {/* Tenure Input */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <label className="text-sm font-semibold text-gray-700 flex items-center space-x-2">
                    <Calendar size={18} className="text-orange-600" />
                    <span>Tenure (Years)</span>
                  </label>
                  <div className="relative">
                    <input
                      type="number"
                      value={loanTenure}
                      onChange={(e) => setLoanTenure(e.target.value)}
                      className="w-24 px-3 py-1 border-2 border-orange-100 rounded-lg font-bold text-orange-700 focus:outline-none focus:border-orange-500 text-right"
                    />
                    <span className="ml-2 font-bold text-orange-700">Yrs</span>
                  </div>
                </div>
                <input
                  type="range"
                  min="1"
                  max="30"
                  step="1"
                  value={loanTenure}
                  onChange={(e) => setLoanTenure(e.target.value)}
                  className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-orange-600"
                />
              </div>

              {/* Breakdown Chart */}
              <div className="bg-orange-50 p-6 rounded-xl border border-orange-100">
                <h4 className="text-sm font-semibold text-gray-700 mb-4">Payment Breakdown</h4>
                <div className="space-y-4">
                  <div>
                    <div className="flex justify-between mb-1">
                      <span className="text-xs text-gray-600">Principal Amount</span>
                      <span className="text-xs font-bold">{principalPercentage.toFixed(1)}%</span>
                    </div>
                    <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                      <div className="h-full bg-orange-500" style={{ width: `${principalPercentage}%` }}></div>
                    </div>
                  </div>
                  <div>
                    <div className="flex justify-between mb-1">
                      <span className="text-xs text-gray-600">Interest Payable</span>
                      <span className="text-xs font-bold">{interestPercentage.toFixed(1)}%</span>
                    </div>
                    <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                      <div className="h-full bg-red-400" style={{ width: `${interestPercentage}%` }}></div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Final Results */}
            <div className="flex flex-col justify-between">
              <div className="bg-gradient-to-br from-orange-500 to-orange-600 rounded-2xl p-8 text-white shadow-lg shadow-orange-200">
                <div className="flex items-center space-x-3 mb-4">
                  <TrendingUp size={28} />
                  <span className="text-lg font-medium opacity-90">Monthly EMI</span>
                </div>
                <div className="text-4xl font-bold mb-2">{formatINR(emi)}</div>
                <p className="text-sm opacity-80 italic">Calculated for {loanTenure} years</p>
              </div>

              <div className="grid gap-4 mt-6">
                <div className="bg-white border-2 border-gray-100 p-5 rounded-2xl">
                  <span className="text-xs text-gray-500 uppercase tracking-wider font-bold block mb-1">Total Interest</span>
                  <span className="text-xl font-bold text-gray-900">{formatINR(totalInterest)}</span>
                </div>
                <div className="bg-white border-2 border-gray-100 p-5 rounded-2xl">
                  <span className="text-xs text-gray-500 uppercase tracking-wider font-bold block mb-1">Total Amount</span>
                  <span className="text-xl font-bold text-gray-900">{formatINR(totalAmount)}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EMICalculator;